// src/pages/wins.tsx
import React, { useMemo, useState } from "react";
import { useCampaignData } from "@/contexts/campaign-data";
import { PerformanceDropsAnalysis } from "@/components/onboarding/PerformanceDropsAnalysis";
import { CampaignSettingsAnalysis } from "@/components/onboarding/CampaignSettingsAnalysis";
import { AccountHealthAnalysis } from "@/components/onboarding/AccountHealthAnalysis";
import { ExecutiveSummary } from "@/components/onboarding/ExecutiveSummary";
import { ActionPlan } from "@/components/main/ActionPlan";
import { NextStepsCard } from "@/components/onboarding/NextStepsCard";
import { DebugCard } from "@/components/onboarding/DebugCard";
import { SHEET_TABS } from "@/lib/constants";
import DraggableVideoPlayer from "@/components/draggable_video_player";

const SHOW_EXEC_SUMMARY = true; // Toggle this flag to show/hide ExecutiveSummary

const QuickWinsPage = () => {
  const { data, loadingStatus, error } = useCampaignData();
  const [minImpressions, setMinImpressions] = useState("1000");
  const [minConversions, setMinConversions] = useState("3");
  const [performanceDropsCount, setPerformanceDropsCount] = useState(0);
  const [geoTargetingIssuesCount, setGeoTargetingIssuesCount] = useState(0);
  const [contentNetworkIssuesCount, setContentNetworkIssuesCount] = useState(0);
  const [accountHealthIssuesCount, setAccountHealthIssuesCount] = useState(0);
  const [searchPartnersIssuesCount, setSearchPartnersIssuesCount] = useState(0);
  const [budgetConstrainedCount, setBudgetConstrainedCount] = useState(0);
  const [disapprovedAdsCount, setDisapprovedAdsCount] = useState(0);
  const [emptyAdGroupsCount, setEmptyAdGroupsCount] = useState(0);
  const [selectedTab, setSelectedTab] = useState<string>("overview");

  // Debug mode state
  const [debugOverride, setDebugOverride] = useState(null);
  const [debugScenario, setDebugScenario] = useState({
    name: "Real Data",
    performanceDrops: 0,
    geoTargeting: 0,
    contentNetwork: 0,
    accountHealth: 0,
    searchPartners: 0,
    budgetConstrained: 0,
    disapprovedAds: 0,
    emptyAdGroups: 0,
    description: "Using actual account data",
  });

  // Use debug values if override is enabled, otherwise use real data
  const effectivePerformanceDrops = debugOverride
    ? debugScenario.performanceDrops
    : performanceDropsCount;
  const effectiveGeoTargeting = debugOverride
    ? debugScenario.geoTargeting
    : geoTargetingIssuesCount;
  const effectiveContentNetwork = debugOverride
    ? debugScenario.contentNetwork
    : contentNetworkIssuesCount;
  const effectiveAccountHealth = debugOverride
    ? debugScenario.accountHealth
    : accountHealthIssuesCount;
  const effectiveSearchPartners = debugOverride
    ? debugScenario.searchPartners
    : searchPartnersIssuesCount;
  const effectiveBudgetConstrained = debugOverride
    ? debugScenario.budgetConstrained
    : budgetConstrainedCount;
  const effectiveDisapprovedAds = debugOverride
    ? debugScenario.disapprovedAds
    : disapprovedAdsCount;
  const effectiveEmptyAdGroups = debugOverride
    ? debugScenario.emptyAdGroups
    : emptyAdGroupsCount;

  const handleDebugScenarioChange = (scenario) => {
    setDebugScenario(scenario);
    setDebugOverride(true);
  };

  const hasDataForDisplay =
    data &&
    data[SHEET_TABS.ON_PERFORMANCE] &&
    data[SHEET_TABS.ON_SETTINGS] &&
    data[SHEET_TABS.ON_DISAPPROVED] &&
    data[SHEET_TABS.ON_EMPTY_AD_GROUPS];

  if (loadingStatus === "initial" || loadingStatus === "refresh" || loadingStatus === "switch" || (!data && !error)) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center space-y-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
          <p className="text-lg text-muted-foreground">Loading your data...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center space-y-4">
          <p className="text-lg text-red-500">Error loading data: {error}</p>
          <a href="/settings" className="text-sm text-muted-foreground underline">Go to Settings</a>
        </div>
      </div>
    );
  }

  if (!hasDataForDisplay) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-lg text-muted-foreground">No data available for this page. Try refreshing your data in Settings.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-7xl mx-auto">
      {/* Video Tutorial Section */}
      <div className="flex justify-end">
        <DraggableVideoPlayer
          videos={[
            {
              id: "1",
              title:
                "Quick Wins Tutorial - You can move this video player & resize it",
              loomId: "ec4064328aa549b9a46930c729bf2766",
            },
          ]}
        />
      </div>

      <div className="relative">
        <div className="text-center">
          <h1 className="text-4xl font-bold tracking-tight text-slate-900">
            Your Quick Wins
          </h1>
          <p className="text-lg text-slate-600 mt-2">
            Immediate opportunities to improve your account performance
          </p>
        </div>
      </div>

      {/* Executive Summary Section */}
      {SHOW_EXEC_SUMMARY && (
        <ExecutiveSummary
          performanceDropsCount={effectivePerformanceDrops}
          geoTargetingIssuesCount={effectiveGeoTargeting}
          contentNetworkIssuesCount={effectiveContentNetwork}
          accountHealthIssuesCount={effectiveEmptyAdGroups}
          searchPartnersIssuesCount={effectiveSearchPartners}
          budgetConstrainedCount={effectiveBudgetConstrained}
          disapprovedAdsCount={effectiveDisapprovedAds}
          selectedTab={selectedTab}
          onTabSelect={setSelectedTab}
        />
      )}

      {/* Tab Content */}
      {selectedTab === "overview" && (
        <div className="space-y-6">
          {/* Performance Drops Analysis */}
          <PerformanceDropsAnalysis
            data={data[SHEET_TABS.ON_PERFORMANCE]}
            minImpressions={minImpressions}
            minConversions={minConversions}
            onMinImpressionsChange={setMinImpressions}
            onMinConversionsChange={setMinConversions}
            onCountChange={setPerformanceDropsCount}
          />

          {/* Campaign Settings Analysis */}
          <CampaignSettingsAnalysis
            data={data[SHEET_TABS.ON_SETTINGS]}
            onGeoTargetingCountChange={setGeoTargetingIssuesCount}
            onContentNetworkCountChange={setContentNetworkIssuesCount}
            onSearchPartnersCountChange={setSearchPartnersIssuesCount}
            onBudgetConstrainedCountChange={setBudgetConstrainedCount}
          />

          {/* Account Health Analysis */}
          <AccountHealthAnalysis
            disapprovedData={data[SHEET_TABS.ON_DISAPPROVED]}
            emptyAdGroupsData={data[SHEET_TABS.ON_EMPTY_AD_GROUPS]}
            onAccountHealthCountChange={setAccountHealthIssuesCount}
            onDisapprovedAdsCountChange={setDisapprovedAdsCount}
            onEmptyAdGroupsCountChange={setEmptyAdGroupsCount}
          />
        </div>
      )}

      {selectedTab === "performance-drops" && (
        <PerformanceDropsAnalysis
          data={data[SHEET_TABS.ON_PERFORMANCE]}
          minImpressions={minImpressions}
          minConversions={minConversions}
          onMinImpressionsChange={setMinImpressions}
          onMinConversionsChange={setMinConversions}
          onCountChange={setPerformanceDropsCount}
        />
      )}

      {(selectedTab === "geo-targeting" ||
        selectedTab === "search-partners" ||
        selectedTab === "display-network" ||
        selectedTab === "budget") && (
        <CampaignSettingsAnalysis
          data={data[SHEET_TABS.ON_SETTINGS]}
          onGeoTargetingCountChange={setGeoTargetingIssuesCount}
          onContentNetworkCountChange={setContentNetworkIssuesCount}
          onSearchPartnersCountChange={setSearchPartnersIssuesCount}
          onBudgetConstrainedCountChange={setBudgetConstrainedCount}
          focusedSection={
            selectedTab as
              | "geo-targeting"
              | "search-partners"
              | "display-network"
              | "budget"
          }
        />
      )}

      {(selectedTab === "disapproved-ads" ||
        selectedTab === "empty-ad-groups") && (
        <AccountHealthAnalysis
          disapprovedData={data[SHEET_TABS.ON_DISAPPROVED]}
          emptyAdGroupsData={data[SHEET_TABS.ON_EMPTY_AD_GROUPS]}
          onAccountHealthCountChange={setAccountHealthIssuesCount}
          onDisapprovedAdsCountChange={setDisapprovedAdsCount}
          onEmptyAdGroupsCountChange={setEmptyAdGroupsCount}
          focusedSection={selectedTab as "disapproved-ads" | "empty-ad-groups"}
        />
      )}

      {/* Debug Card - Development Only */}
      {process.env.NODE_ENV === "development" && (
        <DebugCard
          onScenarioChange={handleDebugScenarioChange}
          currentScenario={debugScenario}
        />
      )}
    </div>
  );
};

export default QuickWinsPage;
